import { useEffect, useState } from 'react';
import { accountApplicationWebSocketService } from './AccountApplicationWebSocketService';
import { useDispatcher } from '../components/hooks';
import { isUserAuthenticated, useStateSelector } from '../state/selectors';
import { WebSocketStatuses } from '../types/WebSocket';
import { useWebSocketReconnection } from '../hooks/useWebSocketReconnection';

const AccountApplicationWebSocketInitializer: React.FC = () => {
  const dispatch = useDispatcher();
  const canConnect = useStateSelector(isUserAuthenticated);
  const webSocketStatus = useStateSelector(s => s.accountApplicationWebSocket.status);
  const apiToken = useStateSelector(s => s.ui.apiToken);

  const [shouldReconnect, setShouldReconnect] = useState<boolean>(false);

  // Custom reconnection delays: 0.5s, 1s, 2s, 4s, 8s, 10s, then repeat
  const reconnectDelays = [500, 1000, 2000, 4000, 8000, 10000];

  const handleReconnect = async () => {
    if (accountApplicationWebSocketService.shouldTryReconnect() && apiToken) {
      await accountApplicationWebSocketService.connect(dispatch, apiToken);
      setShouldReconnect(false);
    }
  };

  const { incrementRetryAttempt } = useWebSocketReconnection({
    delays: reconnectDelays,
    onReconnect: handleReconnect,
    shouldReconnect,
    enabled: !!apiToken,
  });

  useEffect(() => {
    return () => {
      // Clean up WebSocket on unmount if needed
      accountApplicationWebSocketService.closeConnection();
    };
  }, []);

  useEffect(() => {
    if (webSocketStatus === WebSocketStatuses.CLOSED) {
      if (canConnect) {
        const currentDelay = reconnectDelays[0]; // First delay for the message
        const delayInSeconds = currentDelay / 1000;
        const closeMessage = `Account Application Socket is closed. Reconnect will be attempted in ${delayInSeconds} seconds.`;
        console.warn(closeMessage);
        console.log('setting should reconnect to true');
        setShouldReconnect(true);
        incrementRetryAttempt();
      } else {
        const closeMessage = 'Account Application Socket is closed because user can no longer connect';
        console.warn(closeMessage);
      }
    }
  }, [webSocketStatus, canConnect, incrementRetryAttempt]);

  useEffect(() => {
    if (canConnect && apiToken) {
      accountApplicationWebSocketService.connect(dispatch, apiToken);
    } else if (!canConnect && !accountApplicationWebSocketService.shouldTryReconnect()) {
      accountApplicationWebSocketService.closeConnection();
    }
  }, [canConnect, apiToken]);

  return null; // This component does not render anything
};

export default AccountApplicationWebSocketInitializer;
